import React, { useState } from "react";
// Import the Check icon from lucide-react to render list bullet points
import { Check } from "lucide-react";
// Import the PRICES object directly from the Convex backend constants to prevent drift
import { PRICES } from "../../../convex/constants";

/**
 * Prop interface for the PricingTable component.
 * - onSelectPrice: Callback function triggered when a user selects a plan to upgrade. Passes the Stripe Price ID.
 * - currentPriceId: The Stripe Price ID of the user's current subscription, if any.
 * - isSubmitting: Boolean flag indicating if a checkout session creation is currently in progress (disables actions).
 */
export interface PricingTableProps {
  onSelectPrice: (priceId: string) => void;
  currentPriceId?: string;
  isSubmitting?: boolean;
}

/**
 * PricingTable Component
 * Renders the product tiers (Free, Pro, Team) in a responsive grid.
 * Allows toggling between Monthly and Annual plans for the Pro tier.
 */
export const PricingTable: React.FC<PricingTableProps> = ({
  onSelectPrice,
  currentPriceId,
  isSubmitting = false,
}) => {
  // State to track the active billing interval for the Pro plan: 'monthly' or 'annual'
  // Non-coders: Adjust the default value ('monthly') to change the default toggle state.
  const [proInterval, setProInterval] = useState<"monthly" | "annual">("monthly");

  // Determine the active Stripe Price ID for Pro plan based on the toggle interval
  const activeProPriceId =
    proInterval === "monthly" ? PRICES.proMonthly : PRICES.proAnnual;

  // Check if the user's current subscription matches the Pro plan options
  const isCurrentProMonthly = currentPriceId === PRICES.proMonthly;
  const isCurrentProAnnual = currentPriceId === PRICES.proAnnual;
  // If the user's current subscription is Pro, check if it matches the currently toggled option
  const isSubscribedToCurrentPro =
    (proInterval === "monthly" && isCurrentProMonthly) ||
    (proInterval === "annual" && isCurrentProAnnual);

  // Check if the user's current subscription matches the Team plan
  const isSubscribedToTeam = currentPriceId === PRICES.teamAnnual;

  // Check if the user is on the Free tier.
  // In our system, if currentPriceId is not set (undefined or null) or is not any paid Price ID,
  // the user is effectively on the Free or Trial plan.
  const isFreeOrTrialUser =
    !currentPriceId ||
    (currentPriceId !== PRICES.proMonthly &&
      currentPriceId !== PRICES.proAnnual &&
      currentPriceId !== PRICES.teamAnnual);

  return (
    <div className="billing-container">
      {/* Page Header section introducing the pricing models */}
      <div className="billing-header">
        <h1 className="billing-title">Choose Your MultiKeys Plan</h1>
        <p className="billing-subtitle">
          Scale your keyboard and mouse sharing seamlessly across all your devices.
        </p>
      </div>

      {/* Main pricing grid containing the three options */}
      <div className="pricing-grid">
        {/* Tier 1: Free Tier Card */}
        <div className="pricing-card">
          <h2 className="pricing-card-name">Free</h2>
          <p className="pricing-card-description">
            Perfect for casual individual users sharing between a pair of computers.
          </p>
          <div className="pricing-card-price-container">
            <span className="pricing-card-price">$0</span>
            <span className="pricing-card-period">/ month</span>
          </div>
          {/* Empty spacer to align layouts horizontally with cards that have yearly alternatives */}
          <div className="pricing-card-yearly-option"></div>

          {/* List of features included in the Free tier */}
          <ul className="pricing-card-features-list">
            <li className="pricing-card-feature-item">
              <Check className="pricing-card-feature-icon" size={18} />
              <span>Up to 2 connected computers</span>
            </li>
            <li className="pricing-card-feature-item">
              <Check className="pricing-card-feature-icon" size={18} />
              <span>Basic screen layout configuration</span>
            </li>
            <li className="pricing-card-feature-item">
              <Check className="pricing-card-feature-icon" size={18} />
              <span>Standard drag & drop setup</span>
            </li>
          </ul>

          {/* Free Tier Action CTA Button.
              Always disabled as downgrades or staying on Free is managed via Clerk/Stripe portal. */}
          <button
            type="button"
            className="pricing-card-button btn-secondary"
            disabled={true}
          >
            {isFreeOrTrialUser ? "Current Plan" : "Free Plan"}
          </button>
        </div>

        {/* Tier 2: Pro Tier Card (Highlighted / Popular) */}
        <div className="pricing-card is-popular">
          {/* Visual ribbon to drive attention to the popular Pro tier */}
          <div className="pricing-card-badge">Most Popular</div>
          <h2 className="pricing-card-name">Pro</h2>
          <p className="pricing-card-description">
            Unlock unlimited devices, advanced edge configuration, and zero layout caps.
          </p>

          {/* Toggle interval selection group (Monthly vs Annual) */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "0.5rem",
              marginBottom: "1rem",
              padding: "0.25rem",
              backgroundColor: "var(--color-bg-tertiary)",
              borderRadius: "var(--border-radius-sm)",
            }}
          >
            <button
              type="button"
              style={{
                flex: 1,
                padding: "0.35rem 0.75rem",
                borderRadius: "var(--border-radius-sm)",
                border: "none",
                fontSize: "0.85rem",
                fontWeight: 600,
                cursor: "pointer",
                backgroundColor: proInterval === "monthly" ? "var(--color-accent)" : "transparent",
                color: proInterval === "monthly" ? "var(--color-bg-primary)" : "var(--color-text-secondary)",
                transition: "all var(--transition-speed) ease",
              }}
              onClick={() => setProInterval("monthly")}
            >
              Monthly
            </button>
            <button
              type="button"
              style={{
                flex: 1,
                padding: "0.35rem 0.75rem",
                borderRadius: "var(--border-radius-sm)",
                border: "none",
                fontSize: "0.85rem",
                fontWeight: 600,
                cursor: "pointer",
                backgroundColor: proInterval === "annual" ? "var(--color-accent)" : "transparent",
                color: proInterval === "annual" ? "var(--color-bg-primary)" : "var(--color-text-secondary)",
                transition: "all var(--transition-speed) ease",
              }}
              onClick={() => setProInterval("annual")}
            >
              Annual
            </button>
          </div>

          <div className="pricing-card-price-container">
            <span className="pricing-card-price">
              {proInterval === "monthly" ? "$4" : "$29"}
            </span>
            <span className="pricing-card-period">
              {proInterval === "monthly" ? "/ month" : "/ year"}
            </span>
          </div>

          {/* Display annual savings highlight if toggled or available */}
          <div className="pricing-card-yearly-option">
            {proInterval === "annual" ? "Save ~40% vs Monthly billing" : "Save with Annual billing"}
          </div>

          {/* List of features included in the Pro tier */}
          <ul className="pricing-card-features-list">
            <li className="pricing-card-feature-item">
              <Check className="pricing-card-feature-icon" size={18} />
              <strong>Unlimited computers</strong>
            </li>
            <li className="pricing-card-feature-item">
              <Check className="pricing-card-feature-icon" size={18} />
              <span>Priority pairing & instant sync</span>
            </li>
            <li className="pricing-card-feature-item">
              <Check className="pricing-card-feature-icon" size={18} />
              <span>Advanced layout geometry engine</span>
            </li>
            <li className="pricing-card-feature-item">
              <Check className="pricing-card-feature-icon" size={18} />
              <span>Standard email support</span>
            </li>
          </ul>

          {/* Pro Tier CTA Action Button.
              Disabled if we are submitting or if they are already on this specific Pro billing plan. */}
          <button
            type="button"
            className="pricing-card-button btn-primary"
            disabled={isSubmitting || isSubscribedToCurrentPro}
            onClick={() => onSelectPrice(activeProPriceId)}
          >
            {isSubscribedToCurrentPro
              ? "Current Plan"
              : isSubmitting
                ? "Processing..."
                : "Upgrade to Pro"}
          </button>
        </div>

        {/* Tier 3: Team Tier Card */}
        <div className="pricing-card">
          <h2 className="pricing-card-name">Team</h2>
          <p className="pricing-card-description">
            Complete keyboard & mouse control for commercial groups and departments.
          </p>
          <div className="pricing-card-price-container">
            <span className="pricing-card-price">$49</span>
            <span className="pricing-card-period">/ seat / year</span>
          </div>
          <div className="pricing-card-yearly-option">Billed annually</div>

          {/* List of features included in the Team tier */}
          <ul className="pricing-card-features-list">
            <li className="pricing-card-feature-item">
              <Check className="pricing-card-feature-icon" size={18} />
              <strong>Unlimited computers</strong>
            </li>
            <li className="pricing-card-feature-item">
              <Check className="pricing-card-feature-icon" size={18} />
              <span>Central billing portal & multi-seat management</span>
            </li>
            <li className="pricing-card-feature-item">
              <Check className="pricing-card-feature-icon" size={18} />
              <span>Dedicated license provisioning dashboard</span>
            </li>
            <li className="pricing-card-feature-item">
              <Check className="pricing-card-feature-icon" size={18} />
              <span>24/7 dedicated support SLA</span>
            </li>
          </ul>

          {/* Team Tier CTA Action Button.
              Disabled if we are submitting or if the user is already subscribed to the Team plan. */}
          <button
            type="button"
            className="pricing-card-button btn-secondary"
            disabled={isSubmitting || isSubscribedToTeam}
            onClick={() => onSelectPrice(PRICES.teamAnnual)}
          >
            {isSubscribedToTeam
              ? "Current Plan"
              : isSubmitting
                ? "Processing..."
                : "Upgrade to Team"}
          </button>
        </div>
      </div>
    </div>
  );
};
